import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";
import Swal from 'sweetalert2';

const EditAdminCallTemplate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        reminderName: '',
        content: '',
        usedBy: 'CLINIC'
    });
    const [originalData, setOriginalData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                setLoading(true);
                setError(null);

                // Check if ID is valid
                if (!id || id === 'undefined') {
                    throw new Error('Invalid template ID');
                }

                const response = await templateService.getEcxTemplateById(id);

                // Check if response and data exist
                if (!response || !response.data) {
                    throw new Error('No template data received');
                }

                const template = response.data;

                // Handle different response structures
                const templateData = template.data || template; // Handle both {data: {...}} and direct object

                setFormData({
                    reminderName: templateData.reminderName || '',
                    content: templateData.content || '',
                    usedBy: templateData.usedBy || 'CLINIC'
                });

                setOriginalData(templateData);

            } catch (error) {
                console.error('Error fetching template:', error);
                setError(error.message);

                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: error.message || 'Failed to load template. Please try again.',
                    confirmButtonColor: '#dc3545'
                }).then(() => {
                    navigate('/admin-templates');
                });
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTemplate();
        } else {
            setLoading(false);
            setError('No template ID provided');
        }
    }, [id, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSubmitting(true);

            const requestBody = {
                reminderName: formData.reminderName,
                content: formData.content,
                usedBy: formData.usedBy
            };

            const response = await templateService.updateEcxTemplate(id, requestBody);

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'SMS template updated successfully!',
                confirmButtonColor: '#198754',
                timer: 2000,
                showConfirmButton: true
            });

            navigate('/admin-templates');

        } catch (error) {
            console.error('Error updating SMS template:', error);

            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Failed to update SMS template. Please try again.';

            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        const hasChanges =
            formData.content !== originalData?.content ||
            formData.usedBy !== originalData?.usedBy;

        if (hasChanges) {
            Swal.fire({
                title: 'Unsaved Changes',
                text: 'You have unsaved changes. Are you sure you want to leave?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#198754',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, discard changes',
                cancelButtonText: 'No, keep editing'
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/admin-templates');
                }
            });
        } else {
            navigate('/admin-templates');
        }
    };

    const handleReset = () => {
        if (!originalData) return;

        Swal.fire({
            title: 'Reset Changes?',
            text: 'This will restore all fields to their original values.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#198754',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, reset',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                setFormData({
                    reminderName: originalData.reminderName,
                    content: originalData.content,
                    usedBy: originalData.usedBy
                });
            }
        });
    };

    // Add debug logging
    console.log('Template ID:', id);
    console.log('Original Data:', originalData);
    console.log('Form Data:', formData);
    console.log('Error:', error);

    if (loading) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-chat-dots text-success me-2"></i> Edit Call Template</h1>
                </div>
                <Loading />
            </div>
        );
    }

    if (error || !originalData) {
        return (
            <div className="container container-xxl">
                <div className="pagetitle">
                    <h1><i className="bi bi-chat-dots text-success me-2"></i> Edit Call Template</h1>
                </div>
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error || 'Template not found or failed to load.'}
                    <div className="mt-2">
                        <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => navigate('/admin-templates')}
                        >
                            <i className="bi bi-arrow-left me-1"></i>
                            Back to Templates
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const hasChanges =
        formData.content !== originalData.content ||
        formData.usedBy !== originalData.usedBy;

    return (
        <div className="container container-xxl">
            <div className="pagetitle">
                <h1><i className="bi bi-chat-dots text-success me-2"></i> Edit Call Template</h1>
            </div>

            <div className="card card-padding">
                <div className="card-header bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">Editing: {originalData.reminderName}</h5>
                        {hasChanges && (
                            <span className="badge bg-warning text-dark">
                                <i className="bi bi-pencil me-1"></i>
                                Unsaved Changes
                            </span>
                        )}
                    </div>
                </div>

                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        {/* Read-only Template Information */}
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Template Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.reminderName}
                                    readOnly
                                    disabled
                                />
                                <div className="form-text">
                                    Template name cannot be changed
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Medium Type</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={originalData.mediumType || 'SMS'}
                                    readOnly
                                    disabled
                                />
                                <div className="form-text">
                                    Medium type cannot be changed
                                </div>
                            </div>
                        </div>

                        {/* Template Metadata */}
                        <div className="alert alert-info mb-4">
                            <div className="row">
                                <div className="col-md-3">
                                    <strong>Template ID:</strong>
                                    <br />
                                    <span className="small font-monospace">{originalData.id}</span>
                                </div>
                                <div className="col-md-3">
                                    <strong>Created:</strong>
                                    <br />
                                    <span className="small">{originalData.createdAt || 'N/A'}</span>
                                </div>
                                <div className="col-md-3">
                                    <strong>Last Modified:</strong>
                                    <br />
                                    <span className="small">{originalData.modifiedAt || 'N/A'}</span>
                                </div>
                                <div className="col-md-3">
                                    <strong>Reminder Type ID:</strong>
                                    <br />
                                    <span className="small font-monospace">{originalData.reminderTypeId || 'N/A'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Editable Fields */}
                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Used By *</label>
                                <select
                                    className="form-control"
                                    value={formData.usedBy}
                                    onChange={(e) => setFormData({...formData, usedBy: e.target.value})}
                                    required
                                >
                                    <option value="CLINIC">Clinic</option>
                                    <option value="ECX">ECX</option>
                                </select>
                                <div className="form-text">
                                    Select who can use this template
                                </div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="form-label fw-semibold">SMS Message *</label>
                            <textarea
                                className="form-control"
                                rows="6"
                                value={formData.content}
                                onChange={(e) => setFormData({...formData, content: e.target.value})}
                                placeholder="Enter your SMS message (max 480 characters)"
                                maxLength={480}
                                required
                            />
                            <div className="form-text">
                                {formData.content.length}/480 characters. Available
                                variables: {"{PATIENT_NAME}, {CLINIC_NAME}, {APPOINTMENT_DATE}, {APPOINTMENT_TIME}"}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-2 justify-content-between align-items-center">
                            <div>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={handleReset}
                                    disabled={!hasChanges || !originalData}
                                >
                                    <i className="bi bi-arrow-counterclockwise me-1"></i>
                                    Reset Changes
                                </button>
                            </div>

                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    <i className="bi bi-x me-1"></i> Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-success btn-sm"
                                    disabled={!hasChanges || submitting || !originalData}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-lg me-1"></i> Update Template
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditAdminCallTemplate;